import { CommonModule } from '@angular/common';
import type { HttpErrorResponse } from '@angular/common/http';
import type { OnInit } from '@angular/core';
import {
	ChangeDetectorRef,
	Component,
	effect,
	inject,
	input,
	signal,
	ViewChild,
} from '@angular/core';
import type { NgForm } from '@angular/forms';
import { FormGroup } from '@angular/forms';

import type { FormlyFieldConfig, FormlyFormOptions } from '@ngx-formly/core';
import { TranslatePipe } from '@ngx-translate/core';

import { AlertComponent } from '@valk-nx/components/ui-alert/src/lib/alert.component';
import { RecaptchaNotificationComponent } from '@valk-nx/components/ui-recaptcha-notification/src/lib/recaptcha-notification';
import { HOTEL_SLUG } from '@valk-nx/core/lib/core';
import type { CheckedOption } from '@valk-nx/core/lib/interfaces/form.interface';
import { SharedFormlyModule } from '@valk-nx/formly/lib/formly.module';
import { GeneralHelper } from '@valk-nx/helpers/lib/general/general.helper';
import type {
	HotelRecipients,
	Recipients,
} from '@valk-nx/services/form/src/lib/form.service';
import { FormService } from '@valk-nx/services/form/src/lib/form.service';
import { RecaptchaFormService } from '@valk-nx/services/recaptcha-form/src/lib/recaptcha-form.service';
import { shouldUseRecaptchaV2 } from '@valk-nx/services/recaptcha-form/src/lib/recaptcha-utils';
import { StoryblokDirective } from '@valk-nx/storyblok-directives/src/lib/directives/storyblok.directive';
import { StoryblokRootDirective } from '@valk-nx/storyblok-directives/src/lib/directives/storyblok-root.directive';
import { StoryblokHelper } from '@valk-nx/storyblok-helpers/src/lib/general/storyblok.helper';
import type {
	FormInputInterface,
	FormSelectInterface,
} from '@valk-nx/storyblok-types/src/lib/types/storyblok.interface';

@Component({
	selector: 'sb-form',
	templateUrl: './form.html',
	imports: [
		AlertComponent,
		CommonModule,
		TranslatePipe,
		RecaptchaNotificationComponent,
		StoryblokDirective,
		SharedFormlyModule,
	],
	host: { class: 'w-full' },
})
export class FormComponent extends StoryblokRootDirective implements OnInit {
	private readonly cd = inject(ChangeDetectorRef);
	private readonly formService = inject(FormService);
	private readonly recaptchaFormService = inject(RecaptchaFormService);
	readonly hotelSlug = inject(HOTEL_SLUG);

	subject = input.required<string>();
	recipient = input<Recipients>();
	hotelRecipients = input<HotelRecipients | undefined>();
	blocks = input.required<(FormInputInterface & FormSelectInterface)[]>();
	buttonText = input.required<string>();
	successTitle = input.required<string>();
	successContent = input.required<string>();

	useRecaptchaV2 = signal<boolean>(false);

	@ViewChild('formRef') formRef!: NgForm;
	formFields: FormlyFieldConfig[] = [];
	formGroup = new FormGroup({});
	options: FormlyFormOptions = {};
	model = {};

	blocksWithId: (FormInputInterface &
		FormSelectInterface & { id: string })[] = [];
	parsedFormData: {
		id: string;
		label: string;
		value: string | CheckedOption[];
	}[] = [];
	requestSubmitted = false;
	requestSubmitting = false;

	constructor() {
		super();

		effect(() => {
			this.formFields = this.recaptchaFormService.addRecaptchaToForm(
				this.formFields,
				this.useRecaptchaV2(),
			);
		});
	}

	ngOnInit() {
		this.blocksWithId = this.blocks().map((block) => ({
			...block,
			id: block._uid,
		}));
		this.formFields = StoryblokHelper.buildFormlyData(this.blocksWithId);
	}

	async onSubmit(): Promise<void> {
		if (this.formGroup.valid) {
			this.requestSubmitting = true;
			this.parsedFormData = StoryblokHelper.parsedFormData(
				this.formGroup.value,
				this.blocksWithId,
				this.language(),
			).filter((formField) => !!formField); // filter out null value that is set on storyblok.helper.ts line 408

			const emailRecipients: string[] =
				StoryblokHelper.parseEmailRecipientFormData(
					this.blocksWithId,
					this.parsedFormData,
				);

			return this.formService
				.submitCustomForm(
					this.subject(),
					this.parsedFormData,
					this.recipient(),
					this.hotelRecipients(),
					this.hotelSlug,
					emailRecipients,
					this.formGroup.get('token')?.value,
				)
				.then((response) => {
					this.requestSubmitting = false;
					this.requestSubmitted = response.success;
					this.cd.detectChanges();
				})
				.catch((err: HttpErrorResponse) => {
					this.requestSubmitting = false;
					if (shouldUseRecaptchaV2(err)) {
						this.useRecaptchaV2.set(true);
					}
					this.cd.detectChanges();
				});
		} else {
			GeneralHelper.scrollToElementWithClass(
				'form-field-error',
				undefined,
				'input',
			);
		}
	}

	resetForm(): void {
		this.requestSubmitted = false;
		this.requestSubmitting = false;

		this.formGroup.reset();
		this.formRef.resetForm();
	}
}
